import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CalendarIcon, UploadCloud, CheckCircle2, RefreshCw, Loader2, Drama, MapPin, Users, Clapperboard, Film, X, LogIn, Star, Info as InfoIcon,
} from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import SuccessScreen from "@/components/SuccessScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a48e3043-66a2-447d-85d7-dae1e1fd5839/images/b841ddde8a3b8fcc6b56954e5cd376b51b9272988be847287dc401f4cdbf73fe.png";

export const LOGO_IMG =
  "https://customer-assets.emergentagent.com/job_audition-stage-1/artifacts/2bcwv5i5_rr%20logo.png";

const GRADES = ["5th", "6th", "7th", "8th"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_MB = 250;

export const DEFAULT_CONTENT = {
  nav_brand: "Rolling River Day Camp",
  hero_badge: "Drama Intensive",
  hero_title: "Audition for the Navigators Drama Program",
  hero_subtitle:
    "We're excited to invite your child to audition for the 2026 Navigator Musical, Wicked, directed by To The Stage!\n\nTo get started, please fill out the form below and select whether your child is interested in a Main Role, Ensemble, or Stage Crew. For those auditioning for a Main Role or Ensemble, please also submit a 30-second video of your child singing a song from the show (can be any song from Wicked).\n\nNote for Stage Crew: If your child is joining the crew, no video submission is required!\n\nOur directors will review all submissions and announce roles the first week of camp. Break a leg!",
  camper_section_title: "Camper Information",
  camper_section_subtitle: "Tell us about your young star",
  parent_section_title: "Parent / Guardian Information",
  parent_section_subtitle: "So we can reach you about the play",
  session_section_title: "Session Selection",
  session_section_subtitle: "Which session will the camper attend?",
  video_section_title: "Audition Video",
  video_helper:
    "Upload a short 30 second video of your audition. Please sing a song from the show Wicked!",
  commitment_title: "Our Commitment Together",
  commitment_text:
    "By signing below, I understand and agree that participation in the Rolling River Drama Program is a commitment. I agree to attend every scheduled rehearsal and to fully commit to the 6 week drama program for the duration of my selected session. I understand that the success of our camp play depends on every cast member being present and prepared, and that missing rehearsals affects the entire cast. My parent/guardian and I make this commitment together. That Intensive for all 6 weeks. You must be attending camp all Mondays and Wednesday to be able to participate and perform in the show. (Flex campers must have the following dates selected in order to participate: 6/29, 7/1, 7/6, 7/8, 7/13, 7/15, 7/20, 7/22, 7/27, 7/29, 8/3, 8/5, 8/6.",
  commitment_checkbox_label:
    "I, the parent/guardian, have read and agree to this commitment on behalf of my camper.",
  submit_button: "Submit My Audition \ud83c\udfad",
  success_heading: "Thank you,",
  success_message: "Your audition has been submitted.",
  success_subtext: "Our directors will review your video and be in touch with your parent/guardian at",
  success_closing: "Break a leg! \ud83c\udfad",
};

const blankForm = {
  camper_first_name: "", camper_last_name: "", dob: null, grade: "",
  street: "", city: "", state: "", zip: "", camper_phone: "",
  parent_first_name: "", parent_last_name: "", parent_phone: "", parent_email: "",
  session: "", role: "",
};

const Field = ({ label, required, children, testid }) => (
  <div className="flex flex-col" data-testid={testid}>
    <Label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">
      {label} {required && <span className="text-[#C9A227]">*</span>}
    </Label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-xl border-slate-200 bg-slate-50 p-4 h-12 text-base focus-visible:ring-2 focus-visible:ring-[#3556A5]";

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm md:p-8">
    <div className="mb-6 flex items-center gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#3556A5]/10 text-[#3556A5]">
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <h2 className="font-heading text-xl font-semibold text-[#3556A5] sm:text-2xl">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

export default function AuditionForm() {
  const [form, setForm] = useState(blankForm);
  const [sessions, setSessions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [noVideoRole, setNoVideoRole] = useState("stage_crew");
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [season, setSeason] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState({ type: "canvas", data: "", hasSignature: false });
  const [sigKey, setSigKey] = useState(0);

  const [video, setVideo] = useState({ uploading: false, progress: 0, uploaded: false, meta: null, auditionId: null, error: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => {
      setSessions(r.data.sessions || []);
      setRoles(r.data.roles || []);
      if (r.data.no_video_role) setNoVideoRole(r.data.no_video_role);
      if (r.data.content) setContent({ ...DEFAULT_CONTENT, ...r.data.content });
      setSeason(r.data.current_season || null);
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    const allowed = ["mp4", "mov", "m4v", "webm", "avi"];
    if (!allowed.includes(ext)) {
      toast.error("Please choose a video file (mp4, mov, m4v, webm, or avi).");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`That video is too large. Max size is ${MAX_MB} MB.`);
      return;
    }
    setVideo({ uploading: true, progress: 0, uploaded: false, meta: null, auditionId: null, error: "" });
    try {
      const init = await axios.post(`${API}/auditions/upload-video/init`, {
        filename: file.name,
        content_type: file.type || "",
        size: file.size,
      });
      const uploadId = init.data.upload_id;
      const CHUNK = 5 * 1024 * 1024;
      let offset = 0;
      while (offset < file.size) {
        const slice = file.slice(offset, offset + CHUNK);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("offset", String(offset));
        fd.append("chunk", slice, file.name);
        await axios.post(`${API}/auditions/upload-video/chunk`, fd);
        offset += CHUNK;
        const pct = Math.min(99, Math.round((offset / file.size) * 100));
        setVideo((v) => ({ ...v, progress: pct }));
      }
      const r = await axios.post(`${API}/auditions/upload-video/complete`, { upload_id: uploadId });
      setVideo({ uploading: false, progress: 100, uploaded: true, meta: r.data.video, auditionId: r.data.audition_id, error: "" });
      toast.success("Video uploaded ✓");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed. Please try again.";
      setVideo({ uploading: false, progress: 0, uploaded: false, meta: null, auditionId: null, error: msg });
      toast.error(msg);
    }
  };

  const resetVideo = () => {
    setVideo({ uploading: false, progress: 0, uploaded: false, meta: null, auditionId: null, error: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Checklist
  const isStageCrew = form.role === noVideoRole;
  const checks = {
    "Camper name": !!(form.camper_first_name && form.camper_last_name),
    "Date of birth": !!form.dob,
    "Grade": !!form.grade,
    "Home address": !!(form.street && form.city && form.state && form.zip),
    "Parent name": !!(form.parent_first_name && form.parent_last_name),
    "Parent phone": !!form.parent_phone,
    "Parent email": EMAIL_RE.test(form.parent_email),
    "Audition type": !!form.role,
    "Session": !!form.session,
    "Audition video": isStageCrew ? true : video.uploaded,
    "Signature": signature.hasSignature,
    "Agreement checked": agreed,
  };
  const missing = Object.entries(checks).filter(([, ok]) => !ok).map(([k]) => k);
  const canSubmit = missing.length === 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const payload = {
      audition_id: video.uploaded ? video.auditionId : null,
      camper: {
        first_name: form.camper_first_name,
        last_name: form.camper_last_name,
        dob: format(form.dob, "yyyy-MM-dd"),
        grade: form.grade,
        address: { street: form.street, city: form.city, state: form.state, zip: form.zip },
        phone: form.camper_phone || null,
      },
      parent: {
        first_name: form.parent_first_name,
        last_name: form.parent_last_name,
        phone: form.parent_phone,
        email: form.parent_email,
      },
      session: form.session,
      role: form.role,
      commitment_signature_type: signature.type,
      commitment_signature_data: signature.data,
      commitment_signed_by: `${form.parent_first_name} ${form.parent_last_name}`.trim(),
      commitment_agreed: agreed,
    };
    try {
      const r = await axios.post(`${API}/auditions`, payload);
      setResult(r.data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong submitting. Your info is saved — please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setForm(blankForm);
    setAgreed(false);
    setSignature({ type: "canvas", data: "", hasSignature: false });
    setSigKey((k) => k + 1);
    resetVideo();
    setResult(null);
  };

  if (result) return <SuccessScreen result={result} content={content} onAnother={resetAll} />;

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      {/* Top nav */}
      <nav className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-2.5 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src={LOGO_IMG} alt={content.nav_brand} className="h-11 w-11 rounded-full object-contain" />
            <span className="hidden font-heading text-base font-semibold text-[#3556A5] sm:inline">{content.nav_brand}</span>
          </Link>
          <Link
            to="/admin"
            data-testid="nav-admin-login"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-[#3556A5] transition-all hover:border-[#3556A5] hover:bg-[#3556A5]/5"
          >
            <LogIn className="h-4 w-4" /> Admin Login
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#3556A5]/85" />
        <div className="spotlight-beam absolute inset-0" />
        <div className="relative mx-auto max-w-3xl px-6 py-14 text-center md:py-20">
          <img
            src={LOGO_IMG}
            alt="Rolling River Day Camp"
            className="mx-auto mb-5 h-24 w-24 rounded-full bg-white p-1 shadow-lg sm:h-28 sm:w-28"
            data-testid="hero-logo"
          />
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold uppercase tracking-widest text-[#F1D67A] backdrop-blur" data-testid="hero-badge">
            <Drama className="h-4 w-4" /> {content.hero_badge}{season ? ` • ${season}` : ""}
          </div>
          <h1 className="font-heading text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl" data-testid="page-title">
            {content.hero_title}
          </h1>
          <p className="mx-auto mt-5 max-w-xl whitespace-pre-line text-base text-blue-50 sm:text-lg">
            {content.hero_subtitle}
          </p>
        </div>
      </header>

      <main className="mx-auto -mt-6 max-w-3xl space-y-6 px-4 sm:px-6">
        {/* Camper Info */}
        <SectionCard icon={Clapperboard} title={content.camper_section_title} subtitle={content.camper_section_subtitle}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Camper first name" required>
              <Input data-testid="camper-first-name" className={inputCls} value={form.camper_first_name} onChange={(e) => set("camper_first_name", e.target.value)} />
            </Field>
            <Field label="Camper last name" required>
              <Input data-testid="camper-last-name" className={inputCls} value={form.camper_last_name} onChange={(e) => set("camper_last_name", e.target.value)} />
            </Field>
            <Field label="Date of birth" required>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="camper-dob-trigger" className={`${inputCls} justify-start text-left font-normal ${!form.dob && "text-slate-400"}`}>
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#3556A5]" />
                    {form.dob ? format(form.dob, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.dob}
                    onSelect={(d) => set("dob", d)}
                    captionLayout="dropdown-buttons"
                    fromYear={2005}
                    toYear={new Date().getFullYear()}
                    defaultMonth={form.dob || new Date(2014, 0)}
                    disabled={(d) => d > new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Entering grade (fall)" required>
              <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
                <SelectTrigger data-testid="camper-grade" className={inputCls}>
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent>
                  {GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-slate-500">
            <MapPin className="h-4 w-4 text-[#3556A5]" /> Home Address
          </div>
          <div className="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <Field label="Street" required>
                <Input data-testid="addr-street" className={inputCls} value={form.street} onChange={(e) => set("street", e.target.value)} />
              </Field>
            </div>
            <div className="sm:col-span-3">
              <Field label="City" required>
                <Input data-testid="addr-city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="State" required>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="addr-state" className={inputCls}><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="sm:col-span-1">
              <Field label="Zip" required>
                <Input data-testid="addr-zip" inputMode="numeric" className={inputCls} value={form.zip} onChange={(e) => set("zip", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="mt-5">
            <Field label="Camper phone (if applicable)">
              <Input data-testid="camper-phone" className={inputCls} value={form.camper_phone} onChange={(e) => set("camper_phone", e.target.value)} placeholder="(555) 123-4567" />
            </Field>
          </div>
        </SectionCard>

        {/* Parent Info */}
        <SectionCard icon={Users} title={content.parent_section_title} subtitle={content.parent_section_subtitle}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Parent first name" required>
              <Input data-testid="parent-first-name" className={inputCls} value={form.parent_first_name} onChange={(e) => set("parent_first_name", e.target.value)} />
            </Field>
            <Field label="Parent last name" required>
              <Input data-testid="parent-last-name" className={inputCls} value={form.parent_last_name} onChange={(e) => set("parent_last_name", e.target.value)} />
            </Field>
            <Field label="Parent phone" required>
              <Input data-testid="parent-phone" className={inputCls} value={form.parent_phone} onChange={(e) => set("parent_phone", e.target.value)} placeholder="(555) 123-4567" />
            </Field>
            <Field label="Parent email" required>
              <Input
                data-testid="parent-email" type="email"
                className={`${inputCls} ${form.parent_email && !EMAIL_RE.test(form.parent_email) ? "ring-2 ring-red-400" : ""}`}
                value={form.parent_email} onChange={(e) => set("parent_email", e.target.value)} placeholder="you@email.com"
              />
              {form.parent_email && !EMAIL_RE.test(form.parent_email) && (
                <span className="mt-1 text-xs text-red-500">Please enter a valid email address.</span>
              )}
            </Field>
          </div>
        </SectionCard>

        {/* Session */}
        <SectionCard icon={CalendarIcon} title={content.session_section_title} subtitle={content.session_section_subtitle}>
          <RadioGroup value={form.session} onValueChange={(v) => set("session", v)} className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="session-group">
            {sessions.map((s) => (
              <Label
                key={s.value} htmlFor={`session-${s.value}`}
                data-testid={`session-${s.value}`}
                className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-all ${form.session === s.value ? "border-[#3556A5] bg-[#3556A5]/5 ring-1 ring-[#3556A5]" : "border-slate-200 hover:border-[#3556A5]/40"}`}
              >
                <RadioGroupItem value={s.value} id={`session-${s.value}`} />
                <span className="text-base font-semibold text-slate-700">{s.label}</span>
              </Label>
            ))}
          </RadioGroup>
        </SectionCard>

        {/* Audition Type / Role */}
        <SectionCard icon={Star} title="Audition Type" subtitle="What would you like to audition for?">
          <RadioGroup value={form.role} onValueChange={(v) => set("role", v)} className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="role-group">
            {roles.map((r) => (
              <Label
                key={r.value} htmlFor={`role-${r.value}`}
                data-testid={`role-${r.value}`}
                className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-all ${form.role === r.value ? "border-[#3556A5] bg-[#3556A5]/5 ring-1 ring-[#3556A5]" : "border-slate-200 hover:border-[#3556A5]/40"}`}
              >
                <RadioGroupItem value={r.value} id={`role-${r.value}`} />
                <span className="text-base font-semibold text-slate-700">{r.label}</span>
              </Label>
            ))}
          </RadioGroup>
          {isStageCrew && (
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#3556A5]/5 p-3 text-sm text-slate-600" data-testid="stage-crew-note">
              <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#3556A5]" />
              <span>Stage Crew applicants do not need to submit an audition video — just complete the form and sign below!</span>
            </div>
          )}
        </SectionCard>

        {/* Video (hidden for Stage Crew) */}
        {!isStageCrew && (
        <SectionCard icon={Film} title={content.video_section_title} subtitle={`Video files only • Max ${MAX_MB} MB`}>
          <p className="mb-4 rounded-xl bg-[#3556A5]/5 p-3 text-sm text-slate-600">
            {content.video_helper}
          </p>
          <input
            ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/x-m4v,video/webm,video/avi,video/*"
            className="hidden" data-testid="video-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {!video.uploaded && !video.uploading && (
            <button
              type="button" onClick={() => fileInputRef.current?.click()}
              data-testid="video-upload-zone"
              className="flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#3556A5]/40 bg-slate-50 p-10 text-center transition-all hover:border-[#3556A5] hover:bg-[#3556A5]/5"
            >
              <UploadCloud className="h-10 w-10 text-[#3556A5]" />
              <span className="text-base font-semibold text-[#3556A5]">Tap to upload your audition video</span>
              <span className="text-xs text-slate-500">mp4, mov, m4v, webm, avi — up to {MAX_MB} MB</span>
            </button>
          )}

          {video.uploading && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="video-uploading">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#3556A5]">
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading… {video.progress}%
              </div>
              <Progress value={video.progress} className="h-3" />
            </div>
          )}

          {video.uploaded && (
            <div className="flex items-center justify-between rounded-2xl border border-[#10B981]/40 bg-[#10B981]/5 p-5" data-testid="video-uploaded">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-7 w-7 text-[#10B981]" />
                <div>
                  <p className="font-semibold text-slate-800">Video uploaded ✓</p>
                  <p className="max-w-[200px] truncate text-xs text-slate-500 sm:max-w-xs">{video.meta?.original_filename}</p>
                </div>
              </div>
              <Button type="button" variant="ghost" onClick={() => { resetVideo(); fileInputRef.current?.click(); }} data-testid="video-reupload-btn" className="text-[#3556A5]">
                <RefreshCw className="mr-2 h-4 w-4" /> Re-upload
              </Button>
            </div>
          )}
        </SectionCard>
        )}

        {/* Commitment + Signature (prominent, directly above submit) */}
        <section
          className="rounded-3xl border-2 border-[#C9A227] bg-[#C9A227]/[0.07] p-6 shadow-md md:p-8"
          data-testid="commitment-box"
        >
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#C9A227]/20 text-[#A88720]">
              <Drama className="h-6 w-6" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-[#A88720] sm:text-2xl">{content.commitment_title}</h2>
          </div>

          <p className="whitespace-pre-line rounded-2xl bg-white/70 p-5 text-base leading-relaxed text-slate-700">
            {content.commitment_text}
          </p>

          <div className="mt-5 flex items-start gap-3">
            <Checkbox id="agree" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="commitment-checkbox" className="mt-1 data-[state=checked]:bg-[#3556A5] data-[state=checked]:border-[#3556A5]" />
            <Label htmlFor="agree" className="cursor-pointer text-sm font-semibold text-slate-700">
              {content.commitment_checkbox_label}
            </Label>
          </div>

          <div className="mt-6">
            <Label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">
              Parent/Guardian Signature <span className="text-[#C9A227]">*</span>
            </Label>
            <SignaturePad key={sigKey} onChange={setSignature} />
          </div>
        </section>

        {/* Checklist */}
        {missing.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="checklist">
            <p className="mb-2 text-sm font-bold text-slate-700">Still needed before you can submit:</p>
            <div className="flex flex-wrap gap-2">
              {missing.map((m) => (
                <span key={m} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                  <X className="h-3 w-3" /> {m}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="submit-audition-btn"
          className="h-auto w-full rounded-full bg-[#3556A5] py-6 text-lg font-bold text-white shadow-lg transition-all hover:bg-[#2B4584] hover:shadow-xl disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
        >
          {submitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting…</>) : content.submit_button}
        </Button>
        <p className="text-center text-xs text-slate-400">
          Directors review every audition personally. We cannot wait to see you shine!
        </p>
      </main>
    </div>
  );
}
